import { API_BASE_URL } from '../config/api';
import { parseApiResponse } from './apiHelpers';

const LEARN = `${API_BASE_URL}/learn`;
const STUDIO = `${API_BASE_URL}/studio`;

const call = async (url, { method = 'GET', body, headers = {} } = {}) => {
  const res = await fetch(url, {
    method,
    headers: { ...(body !== undefined && { 'Content-Type': 'application/json' }), ...headers },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });
  const data = await parseApiResponse(res);
  return data.data ?? data;
};

/* ---------- Student player ---------- */
export const fetchCourseAccess = (slug, headers) => call(`${LEARN}/${slug}/access`, { headers });
export const fetchCourseOutline = (slug, headers) => call(`${LEARN}/${slug}`, { headers });
export const fetchLessonStream = (lessonId, headers) => call(`${LEARN}/lessons/${lessonId}/stream`, { headers });
export const saveLessonProgress = (lessonId, body, headers) =>
  call(`${LEARN}/lessons/${lessonId}/progress`, { method: 'POST', body, headers });

/* ---------- Notes & bookmarks ---------- */
export const fetchLessonNotes = (lessonId, headers) => call(`${LEARN}/lessons/${lessonId}/notes`, { headers });
export const fetchCourseNotes = (slug, headers) => call(`${LEARN}/${slug}/notes`, { headers });
export const createLessonNote = (lessonId, body, headers) =>
  call(`${LEARN}/lessons/${lessonId}/notes`, { method: 'POST', body, headers });
export const updateLessonNote = (noteId, body, headers) =>
  call(`${LEARN}/notes/${noteId}`, { method: 'PATCH', body, headers });
export const deleteLessonNote = (noteId, headers) => call(`${LEARN}/notes/${noteId}`, { method: 'DELETE', headers });

/* ---------- Resources ---------- */
export const fetchLessonResources = (lessonId, headers) => call(`${LEARN}/lessons/${lessonId}/resources`, { headers });
export const fetchResourceDownload = (resourceId, headers) =>
  call(`${LEARN}/resources/${resourceId}/download`, { headers });

/* ---------- Studio (teacher / admin) ---------- */
export const fetchStudioCourse = (slug, headers) => call(`${STUDIO}/courses/${slug}`, { headers });
export const createStudioModule = (slug, title, headers) =>
  call(`${STUDIO}/courses/${slug}/modules`, { method: 'POST', body: { title }, headers });
export const updateStudioModule = (id, title, headers) =>
  call(`${STUDIO}/modules/${id}`, { method: 'PATCH', body: { title }, headers });
export const deleteStudioModule = (id, headers) => call(`${STUDIO}/modules/${id}`, { method: 'DELETE', headers });
export const createStudioLesson = (moduleId, body, headers) =>
  call(`${STUDIO}/modules/${moduleId}/lessons`, { method: 'POST', body, headers });
export const updateStudioLesson = (id, body, headers) =>
  call(`${STUDIO}/lessons/${id}`, { method: 'PATCH', body, headers });
export const deleteStudioLesson = (id, headers) => call(`${STUDIO}/lessons/${id}`, { method: 'DELETE', headers });
export const deleteStudioLessonVideo = (id, headers) =>
  call(`${STUDIO}/lessons/${id}/video`, { method: 'DELETE', headers });
export const saveStudioOrder = (slug, modules, headers) =>
  call(`${STUDIO}/courses/${slug}/order`, { method: 'PUT', body: { modules }, headers });

export const initVideoUpload = (lessonId, body, headers) =>
  call(`${STUDIO}/lessons/${lessonId}/video`, { method: 'POST', body, headers });
export const presignVideoParts = (uploadId, partNumbers, headers) =>
  call(`${STUDIO}/uploads/${uploadId}/parts`, { method: 'POST', body: { part_numbers: partNumbers }, headers });
export const completeVideoUpload = (uploadId, body, headers) =>
  call(`${STUDIO}/uploads/${uploadId}/complete`, { method: 'POST', body, headers });
export const abortVideoUpload = (uploadId, headers) =>
  call(`${STUDIO}/uploads/${uploadId}/abort`, { method: 'POST', headers });

export const initResourceUpload = (lessonId, body, headers) =>
  call(`${STUDIO}/lessons/${lessonId}/resources`, { method: 'POST', body, headers });
export const completeResourceUpload = (resourceId, headers) =>
  call(`${STUDIO}/resources/${resourceId}/complete`, { method: 'POST', headers });
export const deleteStudioResource = (resourceId, headers) =>
  call(`${STUDIO}/resources/${resourceId}`, { method: 'DELETE', headers });

/* ---------- Account activation ---------- */
export const activateStudentAccount = (body) => call(`${API_BASE_URL}/users/activate`, { method: 'POST', body });

/* ---------- Studio index, accounts and passwords ---------- */
const USERS = `${API_BASE_URL}/users`;
export const fetchStudioCourses = (headers) => call(`${STUDIO}/courses`, { headers });
export const fetchAdminAccounts = (headers) => call(`${USERS}/admin/accounts`, { headers });
export const resetAccountPassword = (userId, headers) =>
  call(`${USERS}/${userId}/reset-password`, { method: 'POST', headers });
export const changeMyPassword = (body, headers) => call(`${USERS}/me/password`, { method: 'PUT', body, headers });
